import { useEffect, useReducer, useMemo, useState, useContext } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  SettingsOutlined as SettingsOutlinedIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useTheme } from "@mui/material/styles";
import useQuery from "../../../../shared/components/hooks/useQuery";
import useMutation from "../../../../shared/components/hooks/useMutation";
import { PropertyContext } from "../../../../contexts/PropertyContext";
import {
  ActionType,
  ConfigurationPreset,
  INITIAL_CONFIG_ID,
  INITIAL_KPI_SETTINGS,
  INITIAL_STAY_DATE,
  KPIOption,
  KpiSettingsDynamic,
  State,
} from "../../../../shared/interfaces/kpi";
import {
  filterConfiguredKpis,
  findInitialStayDate,
  hasSubmenus,
  isKpiConfigEmpty,
  uniqueId,
} from "../../../../shared/utils/kpi";
import KpiConfigPanel from "./KpiConfigPanel";
import { reducer } from "../reducer";
import PresetListPanel from "./PresetListPanel";
import KpiListPanel from "./KpiListPanel";

const initialReducerState: State = {
  savedConfigurations: {},
  selectedConfigId: INITIAL_CONFIG_ID,
  editingPresetDate: null,
  editingKPIKey: null,
  editingKPI: INITIAL_KPI_SETTINGS,
  initialConfigurationsLoaded: false,
  sharedStayDate: INITIAL_STAY_DATE,
};

export default function Settings() {
  const theme = useTheme();
  const { propertyId } = useContext(PropertyContext);
  const { modifyData } = useMutation();
  const [state, dispatch] = useReducer(reducer, initialReducerState);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [presetToDelete, setPresetToDelete] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const {
    data: allKpiOptionsData,
    loading: loadingKpiOptions,
    error: errorKpiOptions,
  } = useQuery(`settings/1`);
  const {
    data: userConfigData,
    loading: loadingUserConfig,
    error: errorUserConfig,
  } = useQuery(`settings/my?propertyId=${propertyId ?? ""}`, undefined, null, {
    enabled: !!propertyId,
  });

  const ALL_KPI_OPTIONS: Record<string, KPIOption> =
    (allKpiOptionsData as any)?.data?.data?.settings || {};
  const selectedPreset = state.savedConfigurations[state.selectedConfigId] || {
    name: "",
    date: null,
    kpiSettings: {},
    stayDate: INITIAL_STAY_DATE,
  };
  const currentKPISettings = state.editingKPIKey
    ? ALL_KPI_OPTIONS[state.editingKPIKey]
    : null;

  const handleApplyChanges = () => {
    if (state.editingKPIKey && currentKPISettings) {
      dispatch({ type: ActionType.APPLY_KPI_CHANGES, currentKPISettings });
    }

    const preset = state.savedConfigurations[state.selectedConfigId];
    const isStayDateChanged =
      JSON.stringify(state.sharedStayDate) !== JSON.stringify(preset.stayDate);

    if (isStayDateChanged) {
      dispatch({
        type: ActionType.APPLY_STAY_DATE_CHANGES,
        stayDate: state.sharedStayDate,
      });
    }
  };

  const isApplyDisabled = useMemo(() => {
    if (!state.editingKPIKey || !currentKPISettings) return true;

    const { stayDate: savedStayDateFromKpi, ...saved } =
      selectedPreset.kpiSettings[state.editingKPIKey] || INITIAL_KPI_SETTINGS;
    const { stayDate: editingStayDateFromKpi, ...editing } = state.editingKPI;

    const isSaved = JSON.stringify(saved) === JSON.stringify(editing);
    const isPresent = state.editingKPIKey in selectedPreset.kpiSettings;
    const isConfigurable = hasSubmenus(currentKPISettings);

    if (isPresent && !isSaved) return false;
    if (!isPresent && isConfigurable && !isKpiConfigEmpty(state.editingKPI))
      return false;
    if (!isPresent && !isConfigurable) return false;

    const kpiSupportsStayDate = Object.keys(ALL_KPI_OPTIONS).some(
      (k) => (ALL_KPI_OPTIONS[k] as any)?.stayDate
    );
    const isStayDateUnchanged =
      JSON.stringify(state.sharedStayDate) ===
      JSON.stringify(selectedPreset.stayDate);

    if (kpiSupportsStayDate && !isStayDateUnchanged) {
      return false;
    }

    return true;
  }, [
    state.editingKPI,
    state.editingKPIKey,
    currentKPISettings,
    selectedPreset,
    state.sharedStayDate,
    ALL_KPI_OPTIONS,
  ]);

  const isPresetSaveDisabled = useMemo(
    () => !selectedPreset.name.trim(),
    [selectedPreset]
  );

  const handleRemoveKpi = () => {
    if (state.editingKPIKey) {
      dispatch({
        type: ActionType.REMOVE_KPI_FROM_PRESET,
        key: state.editingKPIKey,
      });
    }
  };

  const handleDeletePreset = () => {
    if (presetToDelete) {
      dispatch({ type: ActionType.DELETE_PRESET, id: presetToDelete });
      setDeleteDialogOpen(false);
      setPresetToDelete(null);
    }
  };

  const handleFinalSave = async () => {
    try {
      setSaving(true);
      if (!isApplyDisabled) handleApplyChanges();

      const configurationsToSave = state.savedConfigurations;

      const payload = Object.values(configurationsToSave).map((p) => {
        const kpiSettingsWithStayDate = Object.fromEntries(
          Object.entries(p.kpiSettings).map(([kpiKey, kpiConfig]) => {
            if (
              Object.keys(ALL_KPI_OPTIONS).some(
                (k) => k === kpiKey && (ALL_KPI_OPTIONS[k] as any).stayDate
              )
            ) {
              return [kpiKey, { ...kpiConfig, stayDate: p.stayDate }];
            }
            return [kpiKey, kpiConfig];
          })
        );

        return {
          name: (p as any).name,
          date: (p as any).date,
          settings: filterConfiguredKpis(
            kpiSettingsWithStayDate,
            ALL_KPI_OPTIONS
          ),
        };
      });

      await modifyData(
        `${process.env.REACT_APP_API_URL}/settings/user`,
        "post",
        { configurations: payload, propertyId: Number(propertyId) },
        true
      );
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const loading =
    loadingKpiOptions ||
    loadingUserConfig ||
    !state.initialConfigurationsLoaded;
  const error = errorKpiOptions || errorUserConfig;

  // Calculate common dates
  const activeKPIs = Object.values(ALL_KPI_OPTIONS).filter(
    (kpi) => kpi.isActive
  );
  const allDatesArray = activeKPIs.map((kpi) => kpi.date || {});
  const commonDates = Object.keys(allDatesArray[0] || {}).filter((dateKey) =>
    allDatesArray.every((dates) => dates[dateKey])
  );
  const dateOptions = Object.fromEntries(commonDates.map((d) => [d, true]));

  const isFinalizeDisabled = useMemo(() => {
    return (
      !state.editingPresetDate ||
      isPresetSaveDisabled ||
      saving ||
      Object.keys(state.savedConfigurations).length === 0
    );
  }, [
    state.editingPresetDate,
    isPresetSaveDisabled,
    saving,
    state.savedConfigurations,
  ]);

  useEffect(() => {
    if (!allKpiOptionsData || !userConfigData) return;

    const ALL_KPI_OPTIONS_LOCAL: Record<string, KPIOption> =
      (allKpiOptionsData as any)?.data?.data?.settings || {};
    const userSettings =
      (userConfigData as any)?.data?.settings?.configurations || [];
    const savedPresets: Record<string, ConfigurationPreset> = {};
    const initialSharedStayDate = findInitialStayDate(userSettings);

    // Determine default date
    const activeKPIsLocal = Object.values(ALL_KPI_OPTIONS_LOCAL).filter(
      (kpi) => kpi.isActive
    );
    const allDatesArrayLocal = activeKPIsLocal.map((kpi) => kpi.date || {});
    const commonDatesLocal = Object.keys(allDatesArrayLocal[0] || {}).filter(
      (dateKey) => allDatesArrayLocal.every((dates) => dates[dateKey])
    );
    const defaultDate = commonDatesLocal[0] ?? null;

    if (userSettings.length) {
      userSettings.forEach((cfg: any) => {
        const kpiSettings = Object.fromEntries(
          Object.entries(cfg.settings).map(([k, s]: [string, any]) => [
            k,
            { ...s, stayDate: s.stayDate ?? undefined },
          ])
        ) as Record<string, KpiSettingsDynamic>;

        savedPresets[uniqueId()] = {
          name: cfg.name,
          date: cfg.date ?? defaultDate,
          kpiSettings: filterConfiguredKpis(kpiSettings, ALL_KPI_OPTIONS_LOCAL),
          stayDate: initialSharedStayDate,
        };
      });
    } else {
      savedPresets[INITIAL_CONFIG_ID] = {
        name: "Default",
        date: defaultDate,
        kpiSettings: {} as Record<string, KpiSettingsDynamic>,
        stayDate: INITIAL_STAY_DATE,
      };
    }

    dispatch({
      type: ActionType.INIT,
      savedPresets,
      firstKPI:
        Object.keys(ALL_KPI_OPTIONS_LOCAL).find(
          (k) => ALL_KPI_OPTIONS_LOCAL[k].isActive
        ) || null,
      initialStayDate: initialSharedStayDate,
    });
  }, [allKpiOptionsData, userConfigData]);

  if (loading || error) {
    return (
      <Box
        className="dashBoardMain"
        sx={{ pt: -3, px: 2, pb: 2, width: "100%" }}
      >
        <Box
          sx={{
            p: { xs: 0, md: 3 },
            width: "100%",
            backgroundColor: { xs: "transparent", md: "#9E9E9E1A" },
            borderRadius: "12px",
          }}
        >
          <Box
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            alignItems="center"
            justifyContent="center"
            p={4}
          >
            {loading && (
              <Box display="flex" flexDirection="column" alignItems="center">
                <CircularProgress />
                <Typography mt={2}>Loading configuration data...</Typography>
              </Box>
            )}
            {error && (
              <Alert severity="error">
                {(error as any)?.message || "Failed to load settings."}
              </Alert>
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box
        className="dashBoardMain"
        sx={{
          pt: -3,
          pl: { xs: 2, md: 6 },
          pr: { xs: 2, md: 5 },
          pb: 2,
          width: "100%",
        }}
      >
        <Box
          sx={{
            p: { xs: 0, md: 3 },
            width: "100%",
           backgroundColor: { xs: "transparent", md: "#9E9E9E1A" },
            borderRadius: "12px",
          }}
        >
          {/* Title And Actions */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: { xs: "center", md: "space-between" },
              alignItems: "center",
              mb: 3,
              gap: 2,
            }}
          >
            <Box display="flex" alignItems="center">
              <SettingsOutlinedIcon
                sx={{ mr: 1, color: theme.palette.primary.main }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  fontSize: {
                    xs: "1rem",
                    sm: "1.25rem",
                    md: "1.5rem",
                  },
                }}
              >
                Performance Summary Configuration
              </Typography>{" "}
            </Box>
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              sx={{ justifyContent: { xs: "center", md: "flex-start" } }}
            >
              <LoadingButton
                startIcon={<SaveIcon />}
                variant="contained"
                loading={saving}
                onClick={handleFinalSave}
                disabled={isFinalizeDisabled}
              >
                Finalize & Save
              </LoadingButton>
            </Box>
          </Box>

          {/* Content */}
          <Box
            sx={{
              display: "flex",
              minHeight: 500,
              border: { xs: 0, md: 1 },
              borderColor: "divider",
              borderRadius: "12px",
              overflow: "hidden",
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            {/* LEFT - Presets and Date Range */}
            <PresetListPanel
              state={state}
              dispatch={dispatch}
              dateOptions={dateOptions}
              selectedPreset={selectedPreset}
              setDeleteDialogOpen={setDeleteDialogOpen}
              setPresetToDelete={setPresetToDelete}
              isPresetSaveDisabled={isPresetSaveDisabled}
            />

            {/* MIDDLE - KPI List */}
            <KpiListPanel
              state={state}
              dispatch={dispatch}
              ALL_KPI_OPTIONS={ALL_KPI_OPTIONS}
              selectedPreset={selectedPreset}
            />

            {/* RIGHT - KPI Configuration */}
            <Box sx={{ flexGrow: 1, p: { xs: 0, md: 2 } }}>
              {state.editingKPIKey && currentKPISettings ? (
                <KpiConfigPanel
                  state={state}
                  dispatch={dispatch}
                  currentKPISettings={currentKPISettings}
                  selectedPresetName={selectedPreset.name}
                  isApplyDisabled={isApplyDisabled}
                  handleApplyChanges={handleApplyChanges}
                  handleRemoveKpi={handleRemoveKpi}
                />
              ) : (
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  sx={{ p: 4 }}
                >
                  Select a KPI from the middle panel to configure its settings
                  for the preset.
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Delete Preset Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: "12px", p: 2, minWidth: 400 } }}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the preset:{" "}
            <strong>
              {state.savedConfigurations[presetToDelete || ""]?.name ||
                "this preset"}
            </strong>
            ?
          </Typography>
          <Typography variant="caption" color="error">
            This action cannot be undone and the changes will be permanent upon
            Finalize & Save.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeletePreset}
            color="error"
            variant="contained"
            disabled={Object.keys(state.savedConfigurations).length <= 1}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
